/*
 * Copyright (c) 2025 Autire Technologies, LLC. All Rights Reserved.
 *
 * This product and related documentation is protected by copyright and
 * distributed under licenses restricting its use, copying, distribution and
 * decompilation. No part of this product or related documentation may be
 * reproduced in any form by any means without prior written authorization
 * from Autire Technologies, LLC and/or its licensors.
 */

/**
 * @fileoverview Custom encryption and decryption functions for secure data handling
 * @description Implements a proprietary character substitution encryption algorithm
 * with random key generation for enhanced security. The encryption process includes
 * character mapping, random offset calculation, and key embedding. These functions
 * are used throughout the application for securing sensitive data transmission
 * and storage.
 *
 * @module EncryptionFunctions
 * @author PKP Technologies, LLC
 * @since 1.0.0
 * @version 1.0.0
 *
 * @example
 * import { encrypt, decrypt } from './encryption';
 *
 * // Encrypt sensitive data
 * const encrypted = encrypt('sensitive data');
 *
 * // Decrypt data
 * const decrypted = decrypt(encrypted.r);
 *
 * @see {@link utility.js} For Base64 encoding functions
 * @see {@link validation.js} For input validation functions
 */

/**
 * Encrypts data using custom character substitution algorithm
 *
 * @description Uses a proprietary encryption algorithm that combines character mapping,
 * random key generation, and substitution techniques. The algorithm creates a character
 * map from ASCII codes 32-126, shuffles it using a random seed, and applies character
 * substitution with a random offset. Objects are automatically JSON stringified before
 * encryption. The encrypted result includes the key embedded in the output string.
 *
 * @function encrypt
 * @param {string|Object} d - Data to encrypt (strings are encrypted directly, objects are JSON stringified)
 * @returns {Object} Encrypted data object
 * @returns {string} returns.r - Encrypted string with embedded key
 *
 * @example
 * // Encrypt string
 * const encrypted = encrypt('sensitive data');
 * console.log(encrypted.r); // "encrypted_string_with_key"
 *
 * @example
 * // Encrypt object
 * const userData = { userId: '123', role: 'admin' };
 * const encrypted = encrypt(userData);
 * console.log(encrypted.r); // "encrypted_json_string_with_key"
 *
 * @example
 * // Encrypt for API transmission
 * const apiData = {
 *   username: 'user@example.com',
 *   password: 'securePassword123'
 * };
 * const encryptedData = encrypt(apiData);
 *
 * // Send to API
 * fetch('/api/login', {
 *   method: 'POST',
 *   body: JSON.stringify({ data: encryptedData.r })
 * });
 *
 * @throws {Error} When encryption process fails (rare, but possible due to random number generation)
 *
 * @since 1.0.0
 * @author PKP Technologies, LLC
 * @see {@link decrypt} For decryption function
 * @see {@link encodeQueryParamToBase64} For Base64 encoding
 */
export function encrypt(d) {
  let a = '';
  const b = Math.floor(Math.random() * 9) + 1;
  const c = Math.random();
  let k = [...Array(95).keys()].map((n) => {
    return n + 32;
  });
  for (let i = k.length - 1; i > 0; i--)
    [k[i], k[Math.floor(c * (i + 1))]] = [k[Math.floor(c * (i + 1))], k[i]];
  d = typeof d === 'object' && !Array.isArray(d) && d !== null ? JSON.stringify(d) : d;
  for (let i = 0; i < d.length; i++)
    a += k.includes(d.charCodeAt(i))
      ? String.fromCharCode(
          k[
            k.indexOf(d.charCodeAt(i)) + b > k.length - 1
              ? k.indexOf(d.charCodeAt(i)) + b - k.length
              : k.indexOf(d.charCodeAt(i)) + b
          ]
        )
      : d.charAt(i);
  //   return b + c.toFixed(16).replace("0.", "0") + a
  return { r: b + c.toFixed(16).replace('0.', '0') + a };
}

/**
 * Decrypts data using custom character substitution algorithm
 *
 * @description Reverses the encryption process by extracting the embedded key from
 * the encrypted string and applying the inverse character substitution. The function
 * reconstructs the original character map using the stored random seed and applies
 * the reverse offset to restore the original data. This function is the inverse of
 * the encrypt function and must be used with data encrypted by the same algorithm.
 *
 * @function decrypt
 * @param {string} d - Encrypted string with embedded key (format: key + seed + encrypted_data)
 * @returns {string} Decrypted data (original string or JSON string if object was encrypted)
 *
 * @example
 * // Decrypt string
 * const encrypted = encrypt('sensitive data');
 * const decrypted = decrypt(encrypted.r);
 * console.log(decrypted); // "sensitive data"
 *
 * @example
 * // Decrypt object
 * const userData = { userId: '123', role: 'admin' };
 * const encrypted = encrypt(userData);
 * const decrypted = decrypt(encrypted.r);
 * const parsedData = JSON.parse(decrypted);
 * console.log(parsedData); // { userId: '123', role: 'admin' }
 *
 * @example
 * // Decrypt from API response
 * fetch('/api/data')
 *   .then(response => response.json())
 *   .then(data => {
 *     const decrypted = decrypt(data.encryptedData);
 *     const parsedData = JSON.parse(decrypted);
 *     console.log('Decrypted data:', parsedData);
 *   });
 *
 * @throws {Error} When decryption process fails or invalid encrypted data is provided
 *
 * @since 1.0.0
 * @author PKP Technologies, LLC
 * @see {@link encrypt} For encryption function
 * @see {@link decodeQueryParamToBase64} For Base64 decoding
 */
export function decrypt(d) {
  let a = '';
  const b = d.substring(0, 1);
  const c = d.substring(1, 18).replace('0', '0.');
  d = d.substr(18);
  let k = [...Array(95).keys()].map((n) => {
    return n + 32;
  });
  for (let i = k.length - 1; i > 0; i--)
    [k[i], k[Math.floor(c * (i + 1))]] = [k[Math.floor(c * (i + 1))], k[i]];
  for (let i = 0; i < d.length; i++)
    a += k.includes(d.charCodeAt(i))
      ? String.fromCharCode(
          k[
            k.indexOf(d.charCodeAt(i)) - b >= 0
              ? k.indexOf(d.charCodeAt(i)) - b
              : k.length + (k.indexOf(d.charCodeAt(i)) - b)
          ]
        )
      : d.charAt(i);
  return a;
}
